import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { apiService } from '@/services/api';
import { Checkbox } from "@/components/ui/checkbox";
import { ACCESS_AREAS } from "@/constants/accessAreas";

const adminUserSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  role: z.enum(['admin', 'cliente', 'cliente_visual', 'analista'], { required_error: 'Função é obrigatória' }),
  client_ids: z.array(z.number()).optional(),
  allowed_areas: z.array(z.string()).optional(),
}).refine((data) => {
  if (data.role === 'analista' && (!data.allowed_areas || data.allowed_areas.length === 0)) {
    return false;
  }
  return true;
}, {
  message: "Selecione pelo menos uma área de acesso",
  path: ["allowed_areas"],
}).refine((data) => {
  if ((data.role === 'cliente' || data.role === 'cliente_visual') && (!data.client_ids || data.client_ids.length === 0)) {
    return false;
  }
  return true;
}, {
  message: "Selecione ao menos um cliente",
  path: ["client_ids"],
});

type AdminUserFormData = z.infer<typeof adminUserSchema>;

interface AdminUserFormProps {
  onSuccess: () => void;
  initialData?: any;
  isEditing?: boolean;
}

interface Client {
  id: number;
  legal_name: string;
  trade_name?: string;
}

const AdminUserForm: React.FC<AdminUserFormProps> = ({ onSuccess, initialData, isEditing = false }) => {
  const { toast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [clientFilter, setClientFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);

  const form = useForm<AdminUserFormData>({
    resolver: zodResolver(adminUserSchema),
    defaultValues: {
      name: initialData?.name || '',
      email: initialData?.email || '',
      password: '',
      role: initialData?.role || 'cliente',
      client_ids: Array.isArray(initialData?.clients)
        ? initialData.clients.map((c: any) => c.id)
        : (initialData?.client_id ? [initialData.client_id] : []),
      allowed_areas: initialData?.allowed_areas || [],
    },
  });

  const watchRole = form.watch('role');

  // Quando o role mudar para 'analista', definir '/tickets' como padrão se não houver áreas selecionadas
  useEffect(() => {
    if (watchRole === 'analista' && !isEditing) {
      const currentAreas = form.getValues('allowed_areas') || [];
      if (currentAreas.length === 0) {
        form.setValue('allowed_areas', ['/tickets']);
      }
    }
  }, [watchRole, form, isEditing]);


  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        console.log('Buscando clientes...');
        const response = await apiService.getClients();
        console.log('Resposta da API:', response);

        // A API retorna um array diretamente, não um objeto com propriedade clients
        const clientsData = Array.isArray(response) ? response : [];
        setClients(clientsData);
        setFilteredClients(clientsData);

        console.log('Clientes carregados:', clientsData.length);
      } catch (error) {
        console.error('Erro ao buscar clientes:', error);
        toast({
          title: "Erro ao carregar clientes",
          description: "Não foi possível carregar a lista de clientes.",
          variant: "destructive",
        });
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [toast]);

  // Filtrar clientes quando o filtro muda
  useEffect(() => {
    if (!clientFilter.trim()) {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        client.legal_name.toLowerCase().includes(clientFilter.toLowerCase()) ||
        (client.trade_name && client.trade_name.toLowerCase().includes(clientFilter.toLowerCase()))
      );
      setFilteredClients(filtered);
    }
  }, [clientFilter, clients]);

  const onSubmit = async (data: AdminUserFormData) => {
    setLoading(true);
    try {
      const userData = {
        name: data.name,
        email: data.email,
        password: data.password,
        role: data.role,
        client_ids: (data.role === 'cliente' || data.role === 'cliente_visual') ? data.client_ids : undefined,
        allowed_areas: data.role === 'analista' ? data.allowed_areas : undefined,
      };

      if (isEditing && initialData?.id) {
        await apiService.updateUser(initialData.id, userData as any);
        toast({
          title: "Usuário atualizado com sucesso!",
          description: `${data.name} foi atualizado.`,
        });
      } else {
        await apiService.createUser(userData as any);
        toast({
          title: "Usuário criado com sucesso!",
          description: `${data.name} foi adicionado com a função ${data.role}.`,
        });
      }

      form.reset();
      onSuccess();
    } catch (error: any) {
      toast({
        title: isEditing ? "Erro ao atualizar usuário" : "Erro ao criar usuário",
        description: error.message || "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome Completo</FormLabel>
              <FormControl>
                <Input placeholder="Digite o nome completo" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>E-mail</FormLabel>
              <FormControl>
                <Input type="email" placeholder="Digite o e-mail" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {isEditing ? 'Nova Senha (deixe em branco para manter a atual)' : 'Senha'}
              </FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={isEditing ? "Nova senha (opcional)" : "Digite a senha"}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Função</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma função" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                  <SelectItem value="cliente_visual">Cliente visual</SelectItem>
                  <SelectItem value="analista">Analista</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchRole === 'analista' && (
          <FormField
            control={form.control}
            name="allowed_areas"
            render={() => (
              <FormItem>
                <div className="mb-4">
                  <FormLabel className="text-base">Áreas de Acesso</FormLabel>
                </div>
                <div className="grid grid-cols-2 gap-4 border p-4 rounded-md">
                  {ACCESS_AREAS.map((area) => (
                    <FormField
                      key={area.value}
                      control={form.control}
                      name="allowed_areas"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={area.value}
                            className="flex flex-row items-start space-x-3 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(area.value)}
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([...(field.value || []), area.value])
                                    : field.onChange(
                                      field.value?.filter(
                                        (value) => value !== area.value
                                      )
                                    )
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer">
                              {area.label}
                            </FormLabel>
                          </FormItem>
                        )
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {(watchRole === 'cliente' || watchRole === 'cliente_visual') && (
          <>
            {watchRole === 'cliente_visual' && (
              <p className="text-sm text-muted-foreground mb-2">
                Acesso apenas a: Dashboard (quantidades), Cronograma de Envios e Disparo Avulso.
              </p>
            )}
            <div className="space-y-2">
              <FormLabel>Filtrar Clientes</FormLabel>
              <Input
                placeholder="Digite para filtrar clientes..."
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className="mb-2"
              />
            </div>

            <FormField
              control={form.control}
              name="client_ids"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Clientes (selecione um ou mais)</FormLabel>
                  {loadingClients ? (
                    <div className="mt-2">
                      <AtomicLoadingScreen message="Carregando clientes..." className="h-16" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 border p-3 rounded-md max-h-64 overflow-auto">
                      {filteredClients.length === 0 && (
                        <p className="text-sm text-muted-foreground">Nenhum cliente encontrado</p>
                      )}
                      {filteredClients.map((client) => {
                        const checked = (field.value || []).includes(client.id);
                        return (
                          <label key={client.id} className="flex items-start space-x-3 cursor-pointer">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(isChecked) => {
                                const atual = field.value || [];
                                field.onChange(
                                  isChecked
                                    ? [...atual, client.id]
                                    : atual.filter((id: number) => id !== client.id)
                                );
                              }}
                            />
                            <span className="text-sm font-normal leading-tight">
                              {client.legal_name} {client.trade_name ? `(${client.trade_name})` : ''}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <FormMessage />
                  {!loadingClients && clients.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {(field.value || []).length} selecionado(s) — {filteredClients.length} de {clients.length} clientes
                    </p>
                  )}
                </FormItem>
              )}
            />
          </>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => form.reset()}
            disabled={loading}
          >
            Limpar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading
              ? (isEditing ? 'Atualizando...' : 'Criando...')
              : (isEditing ? 'Atualizar Usuário' : 'Criar Usuário')
            }
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AdminUserForm;
